import type { ReactNode } from "react";

/**
 * Standard UI states for all critical screens.
 * Every page must handle all 5 states explicitly.
 */
export type ViewState = "empty" | "loading" | "error" | "partial" | "success";

export interface StateConfig<T> {
  data: T | undefined | null;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  /** Optional: marks data as partial (e.g., some queries succeeded, others failed) */
  isPartial?: boolean;
}

/**
 * Derive the current ViewState from a query/mutation result.
 */
export function deriveState<T>(config: StateConfig<T>): ViewState {
  if (config.isLoading) return "loading";
  if (config.isError) return "error";
  if (config.isPartial) return "partial";
  if (!config.data || (Array.isArray(config.data) && config.data.length === 0)) return "empty";
  return "success";
}

/**
 * Derive state from multiple queries — returns "partial" if some succeeded but not all.
 */
export function deriveCompositeState(configs: StateConfig<unknown>[]): ViewState {
  const anyLoading = configs.some((c) => c.isLoading);
  const anyError = configs.some((c) => c.isError);
  const allError = configs.every((c) => c.isError);
  const anyData = configs.some(
    (c) => c.data && (!Array.isArray(c.data) || c.data.length > 0)
  );

  if (anyLoading && !anyData) return "loading";
  if (allError) return "error";
  if (anyError && anyData) return "partial";
  if (anyLoading && anyData) return "partial";
  if (!anyData) return "empty";
  return "success";
}

/**
 * State message helpers for consistent copy across the app.
 */
export const STATE_MESSAGES = {
  empty: {
    title: "Nada por aqui ainda",
    description: "Quando houver dados, eles aparecerão aqui.",
  },
  error: {
    title: "Algo deu errado",
    description: "Não conseguimos carregar os dados. Tente novamente ou entre em contato com o suporte.",
    retryLabel: "Tentar novamente",
    supportLabel: "Abrir suporte",
    supportPath: "/support",
  },
  partial: {
    title: "Dados parciais",
    description: "Parte dos dados foi carregada. Algumas seções podem estar indisponíveis.",
  },
} as const;

/**
 * Number of skeleton rows for loading state (not spinners).
 */
export const SKELETON_ROWS = 5;
